import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import Card from '../../../shared/components/Card';
import { getApiBaseUrl } from '../../../core/config/api';

const ValidateAccount: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const runValidation = useCallback(async (validationToken: string) => {
    try {
      const apiUrl = getApiBaseUrl();
      const url = `${apiUrl}/api/auth/validate?token=${encodeURIComponent(validationToken)}`;
      const response = await fetch(url, { method: 'GET', credentials: 'include' });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Réponse non-JSON:', text.substring(0, 200));
        setStatus('error');
        setMessage('Réponse serveur invalide. Réessayez plus tard ou contactez le support.');
        return;
      }

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message || 'Votre compte a été activé avec succès.');
        window.setTimeout(() => navigate('/'), 2500);
      } else {
        setStatus('error');
        setMessage(data.error || 'La validation a échoué.');
      }
    } catch (e: any) {
      console.error(e);
      setStatus('error');
      setMessage(e?.message || 'Erreur réseau lors de la validation.');
    }
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Lien de validation invalide : token manquant.');
      return;
    }
    void runValidation(token);
  }, [token, runValidation]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 pt-24 pb-16">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2 text-gradient">Validation du compte</h1>
              <p className="text-gray-400">Activation de votre adresse email</p>
            </div>

            {status === 'loading' && (
              <div className="text-center text-gray-300 py-6">Validation en cours…</div>
            )}

            {status === 'success' && (
              <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-200 text-center">
                {message}
                <p className="mt-3 text-sm text-green-300/90">Redirection vers l&apos;accueil…</p>
              </div>
            )}

            {status === 'error' && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-center">{message}</div>
            )}

            <div className="text-center mt-6">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-primary-400 hover:text-primary-300 text-sm transition-colors"
              >
                Retour à l&apos;accueil
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ValidateAccount;
